import React, { useCallback, useEffect, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useAnalyticsService } from '../../services/analytics.service';
import { ANALYTICS_EVENT_TYPES } from '../../types/analytics.types';
import { useUserState } from '../../providers/user-state.provider.ts';
import { marketingTagsLoadedEventName, trackConfiguredPageViewConversion } from '../../services/marketing.service';
import { sanitizeAnalyticsUrl } from '../../utils/analytics-url';

const minimumEngagementSeconds = 5;

const AnalyticsLayout = () => {
    const location = useLocation();
    const { trackEvent, getSessionId } = useAnalyticsService();
    const { isUserStateLoading } = useUserState();
    const previousPathRef = useRef<string | null>(null);
    const trackEventRef = useRef(trackEvent);
    const sessionIdRef = useRef(getSessionId);
    const lastTrackedPathRef = useRef<string | null>(null);
    const activePathRef = useRef<string | null>(null);
    const activeStartedAtRef = useRef<number | null>(null);

    useEffect(() => {
        trackEventRef.current = trackEvent;
        sessionIdRef.current = getSessionId;
    }, [trackEvent, getSessionId]);

    const flushPageEngagement = useCallback((reason: string) => {
        const path = activePathRef.current;
        const startedAt = activeStartedAtRef.current;
        if (!path || startedAt === null) {
            return;
        }

        const durationSeconds = Math.max(0, Math.round((Date.now() - startedAt) / 1000));
        if (durationSeconds < minimumEngagementSeconds) {
            return;
        }

        // Approximate route dwell time from browser lifecycle events. This is intentionally
        // first-party analytics only; ad pixels should not receive fine-grained page timing.
        void trackEventRef.current({
            eventType: ANALYTICS_EVENT_TYPES.PAGE_ENGAGEMENT,
            path,
            durationSeconds,
            sessionId: sessionIdRef.current(),
            metadata: {
                reason
            }
        });
        activeStartedAtRef.current = Date.now();
    }, []);

    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }, [location.pathname]);

    useEffect(() => {
        if (isUserStateLoading) {
            return;
        }

        const path = sanitizeAnalyticsUrl(`${location.pathname}${location.search}`) || location.pathname || '/';
        if (lastTrackedPathRef.current === path) {
            return;
        }

        flushPageEngagement('route_change');
        lastTrackedPathRef.current = path;
        const referrer = sanitizeAnalyticsUrl(previousPathRef.current) ?? null;
        previousPathRef.current = path;
        activePathRef.current = path;
        activeStartedAtRef.current = Date.now();

        void trackEventRef.current({
            eventType: ANALYTICS_EVENT_TYPES.PAGE_VIEW,
            path,
            referrer,
            sessionId: sessionIdRef.current()
        });
    }, [location.pathname, location.search, isUserStateLoading, flushPageEngagement]);

    useEffect(() => {
        const trackPageViewConversion = () => {
            trackConfiguredPageViewConversion();
        };

        trackPageViewConversion();
        window.addEventListener(marketingTagsLoadedEventName, trackPageViewConversion);

        return () => {
            window.removeEventListener(marketingTagsLoadedEventName, trackPageViewConversion);
        };
    }, [location.pathname, location.search]);

    useEffect(() => {
        if (isUserStateLoading) {
            return;
        }

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'hidden') {
                flushPageEngagement('visibility_hidden');
                return;
            }

            if (activePathRef.current) {
                activeStartedAtRef.current = Date.now();
            }
        };

        const handleBeforeUnload = () => {
            flushPageEngagement('before_unload');
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            flushPageEngagement('layout_unmount');
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [flushPageEngagement, isUserStateLoading]);

    return <Outlet />;
};

export default AnalyticsLayout;
